import { InputHTMLAttributes, KeyboardEvent, forwardRef, useRef } from "react";
import styled from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { focusOutlineAtEdge } from "@pages/AdminDashboard/theme/focusOutline";
import { Button } from "@pages/AdminDashboard/ui/Button";

const { color, density, radius, typeScale } = adminTokens;
const [, spaceSm] = adminTokens.spacing;
// The design's top-bar search: the magnifier sits 14px in and the text starts at
// 38px; with the Enter hint the text leaves 70px at the right.
const iconSize = 16;
const iconOffset = 14;
const textStart = 38;
const textEndPlain = 12;
const textEndWithHint = 70;
const hintFontSize = "11px";
const hintRadius = 6;
// On a phone the search is 44px with 16px text (PHONE-R32, PHONE-R35).
const phoneHeight = 44;
const phoneTextSize = "16px";

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${spaceSm}px;
`;

const Wrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

const Icon = styled.svg`
  position: absolute;
  top: 50%;
  left: ${iconOffset}px;
  transform: translateY(-50%);
  color: ${color.inkSoft};
  pointer-events: none;
`;

const Input = styled.input<{ $hasHint: boolean; $isPhone: boolean }>`
  box-sizing: border-box;
  width: 100%;
  height: ${({ $isPhone }) => ($isPhone ? phoneHeight : density.controlHeight)}px;
  padding: 0 ${({ $hasHint }) => ($hasHint ? textEndWithHint : textEndPlain)}px 0 ${textStart}px;
  border: 1px solid ${color.border};
  border-radius: ${radius.pill}px;
  background-color: ${color.canvas};
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${({ $isPhone }) => ($isPhone ? phoneTextSize : typeScale.body.fontSize)};

  &::placeholder {
    color: ${color.inkSoft};
    opacity: 1;
  }

  &:focus-visible {
    ${focusOutlineAtEdge}
  }

  &:disabled {
    background-color: ${color.warmGray};
    color: ${color.disabledText};
    cursor: not-allowed;
  }
`;

const Hint = styled.span`
  position: absolute;
  top: 50%;
  right: ${spaceSm + 4}px;
  transform: translateY(-50%);
  padding: 1px ${spaceSm - 2}px;
  border: 1px solid ${color.border};
  border-radius: ${hintRadius}px;
  background-color: ${color.surface};
  color: ${color.inkSoft};
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${hintFontSize};
  pointer-events: none;
`;

type SearchAction = { label: string } & (
  | { isPending?: false; pendingLabel?: string }
  | { isPending: true; pendingLabel: string }
);

interface ActionButtonProps {
  action: SearchAction;
  onActivate: () => void;
}

const ActionButton = ({ action, onActivate }: ActionButtonProps) => {
  const { label, ...pendingState } = action;

  return (
    <Button look="primary" onClick={onActivate} {...pendingState}>
      {label}
    </Button>
  );
};

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "role" | "aria-label"> {
  // Names the control for a screen reader; it is not shown (SEARCH-R4).
  label: string;
  onSearch: (value: string) => void;
  hasEnterHint?: boolean;
  action?: SearchAction;
}

// The search of the top bar and of Find: a pill with a magnifier, an optional
// Enter hint and an optional primary Button (SEARCH-R1…R9). Pressing Enter or
// activating the Button fires the search with what was typed.
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ label, onSearch, hasEnterHint = false, action, onKeyDown, ...inputProps }, ref) => {
    const isPhone = useIsPhoneLayout();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const setRefs = (element: HTMLInputElement | null) => {
      inputRef.current = element;
      if (typeof ref === "function") ref(element);
      else if (ref) ref.current = element;
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);
      if (event.key !== "Enter" || event.nativeEvent.isComposing) return;

      onSearch(event.currentTarget.value);
    };

    return (
      <Row>
        <Wrapper>
          <Icon
            aria-hidden="true"
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </Icon>
          <Input
            {...inputProps}
            ref={setRefs}
            type="text"
            role="searchbox"
            aria-label={label}
            $hasHint={hasEnterHint}
            $isPhone={isPhone}
            onKeyDown={handleKeyDown}
          />
          {hasEnterHint && <Hint aria-hidden="true">Enter</Hint>}
        </Wrapper>
        {action && (
          <ActionButton
            action={action}
            onActivate={() => onSearch(inputRef.current?.value ?? "")}
          />
        )}
      </Row>
    );
  }
);

SearchInput.displayName = "SearchInput";
